import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileEdit, Plus, Trash2, ToggleLeft, ToggleRight, Save, X, ChevronDown } from 'lucide-react';
import { useAdminT } from '@/lib/adminTranslations';

const API_BASE = '';

export default function AdminPromptManager() {
  const t = useAdminT();
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [prompts, setPrompts] = useState([]);
  const [defaults, setDefaults] = useState({ page_types: [], block_types: [], languages: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterLang, setFilterLang] = useState('');

  const [form, setForm] = useState({
    prompt_key: '', page_type: 'match', block_type: 'intro_paragraph',
    language: 'en', prompt_text: '', system_prompt_text: '', is_active: true,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('page_type', filterType);
      if (filterLang) params.append('language', filterLang);
      const [pRes, dRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/seo/prompts?${params}`, { headers }),
        fetch(`${API_BASE}/api/admin/seo/prompts/defaults`, { headers }),
      ]);
      const pData = await pRes.json();
      const dData = await dRes.json();
      setPrompts(pData.prompts || []);
      setDefaults(dData);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, filterType, filterLang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.prompt_key) return;
    await fetch(`${API_BASE}/api/admin/seo/prompts`, {
      method: 'POST', headers, body: JSON.stringify(form),
    });
    setCreating(false);
    setForm({ prompt_key: '', page_type: 'match', block_type: 'intro_paragraph', language: 'en', prompt_text: '', system_prompt_text: '', is_active: true });
    fetchData();
  };

  const handleUpdate = async (promptKey, language) => {
    await fetch(`${API_BASE}/api/admin/seo/prompts/${promptKey}?language=${language}`, {
      method: 'PUT', headers, body: JSON.stringify(form),
    });
    setEditing(null);
    fetchData();
  };

  const handleDelete = async (promptKey, language) => {
    if (!window.confirm('Delete this prompt?')) return;
    await fetch(`${API_BASE}/api/admin/seo/prompts/${promptKey}?language=${language}`, {
      method: 'DELETE', headers,
    });
    fetchData();
  };

  const handleToggle = async (promptKey, language) => {
    await fetch(`${API_BASE}/api/admin/seo/prompts/${promptKey}/toggle?language=${language}`, {
      method: 'POST', headers,
    });
    fetchData();
  };

  const startEdit = (prompt) => {
    setEditing(`${prompt.prompt_key}:${prompt.language}`);
    setForm({ ...prompt });
  };

  if (loading) return <div className="p-6 text-slate-400">{t.loading}</div>;

  return (
    <div className="space-y-6" data-testid="admin-prompt-manager">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100" style={{ fontFamily: 'var(--font-heading)' }}>
          <FileEdit className="w-6 h-6 inline mr-2 text-emerald-400" /> {t.aiPromptManager}
        </h1>
        <Button size="sm" onClick={() => setCreating(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="create-prompt-btn">
          <Plus className="w-4 h-4 mr-1" /> {t.newPrompt}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300" data-testid="filter-page-type">
          <option value="">{t.allTypes}</option>
          {defaults.page_types?.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterLang} onChange={e => setFilterLang(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300" data-testid="filter-language">
          <option value="">{t.allLanguages}</option>
          {defaults.languages?.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </select>
      </div>

      {/* Create Form */}
      {creating && (
        <div className="bg-slate-900/80 border border-emerald-500/30 rounded-xl p-5 space-y-4" data-testid="create-prompt-form">
          <h2 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider">{t.createPrompt}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input placeholder="prompt_key (e.g. match_intro)" value={form.prompt_key}
              onChange={e => setForm({ ...form, prompt_key: e.target.value })}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="input-prompt-key" />
            <select value={form.page_type} onChange={e => setForm({ ...form, page_type: e.target.value })}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200">
              {defaults.page_types?.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={form.block_type} onChange={e => setForm({ ...form, block_type: e.target.value })}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200">
              {defaults.block_types?.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200">
              {defaults.languages?.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
          </div>
          <textarea placeholder="System prompt (optional override)" value={form.system_prompt_text}
            onChange={e => setForm({ ...form, system_prompt_text: e.target.value })} rows={3}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 font-mono" />
          <textarea placeholder="User prompt text (use {home}, {away}, {league}, etc. as variables)" value={form.prompt_text}
            onChange={e => setForm({ ...form, prompt_text: e.target.value })} rows={6}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 font-mono" data-testid="input-prompt-text" />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="save-prompt-btn">
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCreating(false)} className="text-slate-400">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Prompts List */}
      <div className="space-y-3">
        {prompts.length === 0 ? (
          <div className="text-center text-slate-500 py-8">No prompts found. Create your first prompt to customize AI content generation.</div>
        ) : prompts.map((p, i) => {
          const editKey = `${p.prompt_key}:${p.language}`;
          const isEditing = editing === editKey;
          return (
            <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4" data-testid={`prompt-item-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-mono font-semibold text-slate-200">{p.prompt_key}</span>
                  <Badge variant="outline" className="border-slate-700 text-slate-400">{p.page_type}</Badge>
                  <Badge variant="outline" className="border-slate-700 text-slate-400">{p.block_type}</Badge>
                  <Badge variant="outline" className={p.is_active ? 'border-emerald-500/30 text-emerald-400' : 'border-red-500/30 text-red-400'}>
                    {p.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Badge variant="outline" className="border-blue-500/30 text-blue-400">{p.language?.toUpperCase()}</Badge>
                  <span className="text-[10px] text-slate-600">v{p.version || 1}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => handleToggle(p.prompt_key, p.language)}
                    className="text-slate-400 hover:text-emerald-400 h-7 px-2" data-testid={`toggle-prompt-${i}`}>
                    {p.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => isEditing ? setEditing(null) : startEdit(p)}
                    className="text-slate-400 hover:text-blue-400 h-7 px-2" data-testid={`edit-prompt-${i}`}>
                    <FileEdit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(p.prompt_key, p.language)}
                    className="text-slate-400 hover:text-red-400 h-7 px-2" data-testid={`delete-prompt-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {!isEditing && p.prompt_text && (
                <div className="text-xs text-slate-500 font-mono bg-slate-800/50 rounded-lg p-2 mt-2 max-h-20 overflow-hidden">
                  {p.prompt_text.substring(0, 200)}{p.prompt_text.length > 200 ? '...' : ''}
                </div>
              )}

              {isEditing && (
                <div className="space-y-3 mt-3 border-t border-slate-800 pt-3">
                  <textarea value={form.system_prompt_text || ''} onChange={e => setForm({ ...form, system_prompt_text: e.target.value })}
                    placeholder="System prompt override (optional)" rows={3}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 font-mono" />
                  <textarea value={form.prompt_text || ''} onChange={e => setForm({ ...form, prompt_text: e.target.value })}
                    placeholder="Prompt text" rows={6}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 font-mono" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleUpdate(p.prompt_key, p.language)} className="bg-blue-600 hover:bg-blue-500 text-white">
                      <Save className="w-3 h-3 mr-1" /> Update
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="text-slate-400">Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
